import React, { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Compass, ArrowRight, CheckCircle, Calendar, Users, Star, Shield, BookOpen } from 'lucide-react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { useToolTheme } from '@/contexts/ThemeContext';

export default function AlignPage() {
  const { toolColors } = useToolTheme();
  
  const programFeatures = [
    {
      title: "Plan personnalisé",
      description: "Un programme d'alignement sur mesure selon votre type énergétique et vos objectifs personnels.",
      icon: <Compass className="w-6 h-6" style={{ color: toolColors.primary }} />
    },
    {
      title: "Exercices quotidiens",
      description: "Des exercices pratiques pour harmoniser votre énergie et développer votre conscience énergétique.",
      icon: <Calendar className="w-6 h-6" style={{ color: toolColors.primary }} />
    },
    {
      title: "Accompagnement individuel",
      description: "Des sessions de coaching personnalisées pour vous guider dans votre parcours d'alignement.",
      icon: <Users className="w-6 h-6" style={{ color: toolColors.primary }} />
    },
    {
      title: "Suivi de progression",
      description: "Des outils de suivi pour mesurer votre évolution et ajuster votre programme si nécessaire.",
      icon: <Star className="w-6 h-6" style={{ color: toolColors.primary }} />
    }
  ];

  const programModules = [
    {
      number: "01",
      title: "Découverte de votre design",
      description: "Comprendre en profondeur votre type énergétique, vos centres, canaux et autorités.",
      duration: "2 semaines"
    },
    {
      number: "02",
      title: "Alignement quotidien",
      description: "Intégrer des pratiques d'alignement dans votre quotidien pour harmoniser votre énergie.",
      duration: "3 semaines"
    },
    {
      number: "03",
      title: "Transformation des blocages",
      description: "Identifier et transformer les blocages énergétiques qui limitent votre potentiel.",
      duration: "4 semaines"
    },
    {
      number: "04",
      title: "Manifestation alignée",
      description: "Apprendre à manifester vos désirs en harmonie avec votre design énergétique.",
      duration: "3 semaines"
    }
  ];

  const testimonials = [
    {
      name: "Sophie M.",
      type: "Projector",
      content: "Le programme Align a complètement transformé ma relation à mon énergie. J'ai enfin compris comment fonctionner en harmonie avec mon design de Projector et ma vie professionnelle s'en est trouvée métamorphosée."
    },
    {
      name: "Thomas L.",
      type: "Generator",
      content: "Grâce à Align, j'ai appris à reconnaître et suivre mon sacral. Je ne m'épuise plus dans des projets qui ne me correspondent pas et je ressens une satisfaction profonde dans mes activités quotidiennes."
    },
    {
      name: "Marie D.",
      type: "Manifesting Generator",
      content: "Ce programme m'a permis de comprendre pourquoi je sautais constamment d'un projet à l'autre. Maintenant, j'utilise cette énergie de manière constructive et je termine enfin ce que j'entreprends !"
    }
  ];

  // Animation controls
  const modulesRef = useRef(null);
  const testimonialsRef = useRef(null);
  const registrationRef = useRef(null);
  const faqRef = useRef(null);
  
  const modulesControls = useAnimation();
  const testimonialsControls = useAnimation();
  const registrationControls = useAnimation();
  const faqControls = useAnimation();
  
  const modulesInView = useInView(modulesRef, { once: false, threshold: 0.2 });
  const testimonialsInView = useInView(testimonialsRef, { once: false, threshold: 0.2 });
  const registrationInView = useInView(registrationRef, { once: false, threshold: 0.2 });
  const faqInView = useInView(faqRef, { once: false, threshold: 0.2 });
  
  useEffect(() => {
    if (modulesInView) {
      modulesControls.start({ opacity: 1, y: 0 });
    }
    if (testimonialsInView) {
      testimonialsControls.start({ opacity: 1, y: 0 });
    }
    if (registrationInView) {
      registrationControls.start({ opacity: 1, y: 0 });
    }
    if (faqInView) {
      faqControls.start({ opacity: 1, y: 0 });
    }
  }, [modulesInView, testimonialsInView, registrationInView, faqInView, modulesControls, testimonialsControls, registrationControls, faqControls]);

  return (
    <motion.div 
      className="min-h-screen pt-20 px-4 tool-background align-background"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Hero Section */}
        <div className="text-center space-y-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-block p-4 bg-white rounded-2xl shadow-xl mb-4"
          >
            <Compass className="w-12 h-12" style={{ color: toolColors.primary }} />
          </motion.div>
          <motion.h1 
            className="text-4xl font-bold hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ color: toolColors.primary }}
          >
            Programme Align
          </motion.h1>
          <motion.p 
            className="text-xl max-w-3xl mx-auto hero-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Un parcours transformationnel pour aligner votre vie avec votre design énergétique unique
          </motion.p>
          <motion.div 
            className="inline-block px-4 py-2 rounded-full font-medium back-button-enhanced"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ 
              backgroundColor: `${toolColors.primary}15`,
              color: toolColors.primary
            }}
          >
            En développement
          </motion.div>
        </div>

        {/* Main Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="tool-card"
        >
          <Card className="bg-white/90 backdrop-blur-sm overflow-hidden">
            <div className="h-2" style={{ background: `linear-gradient(to right, ${toolColors.primary}, ${toolColors.secondary})` }}></div>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-2xl font-bold mb-6 hero-title" style={{ color: toolColors.primary }}>Transformez votre vie en alignant votre énergie</h2>
                  <p className="text-gray-800 mb-6 text-enhanced">
                    Align est un programme complet de 12 semaines conçu pour vous aider à vivre en harmonie avec votre design énergétique. À travers des exercices pratiques, des sessions de coaching et des outils de suivi, vous apprendrez à reconnaître, respecter et optimiser votre énergie unique.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-enhanced">Programme personnalisé selon votre type Human Design</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-enhanced">Accompagnement individuel par un coach certifié</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-enhanced">Outils pratiques pour intégrer les principes au quotidien</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="relative">
                    <div className="w-64 h-64 rounded-full flex items-center justify-center" style={{ backgroundColor: `${toolColors.primary}15` }}>
                      <div className="w-48 h-48 rounded-full flex items-center justify-center" style={{ backgroundColor: `${toolColors.primary}25` }}>
                        <div className="w-40 h-40 rounded-full flex items-center justify-center" style={{ backgroundColor: `${toolColors.primary}35` }}>
                          <Compass className="w-20 h-20" style={{ color: toolColors.primary }} />
                        </div>
                      </div>
                    </div>
                    <div className="absolute top-0 right-0 w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                      <Star className="w-8 h-8 text-purple-600" />
                    </div>
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <Shield className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Program Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {programFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
            >
              <Card className="hover:shadow-lg transition-all bg-white/90 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col h-full">
                    <div className="p-3 rounded-lg self-start mb-4" style={{ backgroundColor: `${toolColors.primary}15` }}>
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-enhanced" style={{ color: toolColors.primary }}>{feature.title}</h3>
                    <p className="text-gray-800 flex-grow text-enhanced">{feature.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Program Modules */}
        <motion.div
          ref={modulesRef}
          initial={{ opacity: 0, y: 30 }}
          animate={modulesControls}
          className="tool-card"
        >
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-center hero-title" style={{ color: toolColors.primary }}>Structure du Programme</h2>
              
              <div className="space-y-8">
                {programModules.map((module, index) => (
                  <motion.div 
                    key={index} 
                    className="flex flex-col md:flex-row gap-6 items-start slide-up"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl shrink-0 font-bold" 
                      style={{ 
                        backgroundColor: `${toolColors.primary}15`,
                        color: toolColors.primary
                      }}
                    >
                      {module.number}
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-semibold mb-2 text-enhanced" style={{ color: toolColors.primary }}>{module.title}</h3>
                      <p className="text-gray-800 mb-2 text-enhanced">{module.description}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{module.duration}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          ref={testimonialsRef}
          initial={{ opacity: 0, y: 30 }}
          animate={testimonialsControls}
          className="tool-card"
        >
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-center slide-up hero-title" style={{ color: toolColors.primary }}>Témoignages</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 * index }}
                  >
                    <Card className="hover:shadow-md transition-all bg-white/95">
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          <p className="text-gray-800 italic text-enhanced">"{testimonial.content}"</p>
                          <div>
                            <p className="font-semibold text-enhanced" style={{ color: toolColors.primary }}>{testimonial.name}</p>
                            <p className="text-sm text-gray-600">{testimonial.type}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Pre-registration */}
        <motion.div
          ref={registrationRef}
          initial={{ opacity: 0, y: 30 }}
          animate={registrationControls}
          className="tool-card"
        >
          <Card style={{ 
            background: `linear-gradient(to right, ${toolColors.primary}, ${toolColors.secondary})`,
            color: 'white'
          }}>
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-4 slide-up hero-title text-white">Pré-inscrivez-vous dès maintenant</h2>
              <p className="text-lg mb-6 max-w-2xl mx-auto slide-up hero-text text-white" style={{ animationDelay: '0.1s' }}>
                Le programme Align est actuellement en développement. Inscrivez-vous pour être informé de son lancement et bénéficier d'une offre spéciale pour les premiers participants.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto slide-up" style={{ animationDelay: '0.2s' }}>
                <input
                  type="email"
                  placeholder="Votre adresse email"
                  className="flex-grow px-4 py-2 border rounded-md focus:ring-2 focus:border-transparent text-gray-800"
                  style={{ 
                    '--tw-ring-color': toolColors.primary,
                  } as React.CSSProperties}
                />
                <Button className="bg-white hover:bg-gray-100 back-button-enhanced" style={{ color: toolColors.primary }}>
                  Je m'inscris
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* FAQ */}
        <motion.div
          ref={faqRef}
          initial={{ opacity: 0, y: 30 }}
          animate={faqControls}
          className="tool-card"
        >
          <Card className="bg-white/90">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-center slide-up hero-title" style={{ color: toolColors.primary }}>Questions fréquentes</h2>
              
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4 slide-up">
                  <h3 className="font-semibold text-lg mb-2 text-enhanced" style={{ color: toolColors.primary }}>Quand sera disponible le programme Align ?</h3>
                  <p className="text-gray-800 text-enhanced">Le programme Align est actuellement en phase de développement. Le lancement est prévu pour le premier trimestre 2026. Vous pouvez vous pré-inscrire pour être informé de son lancement.</p>
                </div>
                
                <div className="border-b border-gray-200 pb-4 slide-up" style={{ animationDelay: '0.1s' }}>
                  <h3 className="font-semibold text-lg mb-2 text-enhanced" style={{ color: toolColors.primary }}>Le programme est-il adapté à tous les types Human Design ?</h3>
                  <p className="text-gray-800 text-enhanced">Oui, le programme Align est personnalisé selon votre type énergétique (Manifestor, Generator, Manifesting Generator, Projector ou Reflector) et prend en compte vos spécificités.</p>
                </div>
                
                <div className="border-b border-gray-200 pb-4 slide-up" style={{ animationDelay: '0.2s' }}>
                  <h3 className="font-semibold text-lg mb-2 text-enhanced" style={{ color: toolColors.primary }}>Faut-il avoir des connaissances préalables en Human Design ?</h3>
                  <p className="text-gray-800 text-enhanced">Non, aucune connaissance préalable n'est nécessaire. Le programme commence par les fondamentaux et vous guide pas à pas dans la découverte de votre design.</p>
                </div>
                
                <div className="border-b border-gray-200 pb-4 last:border-0 slide-up" style={{ animationDelay: '0.3s' }}>
                  <h3 className="font-semibold text-lg mb-2 text-enhanced" style={{ color: toolColors.primary }}>Quel est l'investissement pour le programme ?</h3>
                  <p className="text-gray-800 text-enhanced">Les tarifs seront communiqués lors du lancement. Les personnes pré-inscrites bénéficieront d'une offre spéciale.</p>
                </div>
              </div>
              
              <div className="text-center mt-6 slide-up" style={{ animationDelay: '0.4s' }}>
                <Button asChild variant="outline" className="gap-2 back-button-enhanced" style={{ 
                  borderColor: toolColors.primary,
                  color: toolColors.primary
                }}>
                  <a href="#" className="flex items-center">
                    <BookOpen className="w-4 h-4" />
                    En savoir plus sur le Human Design
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  );
}